import React from 'react';
import styles from './step5.module.scss';
import Prompt from 'components/form/prompt/prompt';
import * as Yup from 'yup';
import FormBuilder from 'components/form/builder/builder';
import Select from 'components/form/select/select';

// All YES/NO Prompts are required
const schemaShape = {
  Violation: Yup.boolean().required('Required'),
  ViolationExplanation: Yup.string(),
  EAndOClaimsInPastFive: Yup.boolean().required('Required'),
  EAndOClaimsInPastFiveExplanation: Yup.string(),
  BankruptcyInPastFive: Yup.boolean().required('Required'),
  BankruptcyInPastFiveExplanation: Yup.string(),
  OpenLiens: Yup.boolean().required('Required'),
  OpenLiensExplanation: Yup.string(),
  StatementEmail: Yup.string()
    .trim()
    .email('Invalid email'),
  HowDidYouHearAboutUs: Yup.string()
    .trim().required()
};

const Step5 = props => {
  const initialValues = {
    // false values here correspond to 'NO' on YES/NO prompts
    Violation: false,
    ViolationExplanation: '',
    EAndOClaimsInPastFive: false,
    EAndOClaimsInPastFiveExplanation: '',
    BankruptcyInPastFive: false,
    BankruptcyInPastFiveExplanation: '',
    OpenLiens: false,
    OpenLiensExplanation: '',
    StatementEmail: '',
    HowDidYouHearAboutUs: '',
    HowDidYouHearAboutUsOther: '',
    ...props.values
  };

  const elements = [
    {
      Component: Prompt,
      props: {
        options: [
          { name: 'YES', value: true },
          { name: 'NO', value: false }
        ]
      },
      label:
        'Has your agency or any partners, officers, owners, or producers of the agency ever been subject to any Department of Insurance action regarding a violation?',
      name: 'Violation',
      conditionaler: true // Element below is conditional on this one
    },
    {
      placeholder: 'Please Explain',
      name: 'ViolationExplanation',
      label: 'Please Explain',
      conditional: { name: 'Violation', value: true },
      maxLength: 500
    },
    {
      Component: Prompt,
      props: {
        options: [
          { name: 'YES', value: true },
          { name: 'NO', value: false }
        ]
      },
      label:
        'Have there been any Errors & Omissions (E & O) claims made against your agency, partners, officers, owners, or producers in the past five (5) years?',
      name: 'EAndOClaimsInPastFive',
      conditionaler: true // Element below is conditional on this one
    },
    {
      placeholder: 'Please Explain',
      name: 'EAndOClaimsInPastFiveExplanation',
      label: 'Please Explain',
      conditional: { name: 'EAndOClaimsInPastFive', value: true },
      maxLength: 500
    },
    {
      Component: Prompt,
      props: {
        options: [
          { name: 'YES', value: true },
          { name: 'NO', value: false }
        ]
      },
      label:
        'Has your agency and/or partners, officers, or owners filed for bankruptcy in the past five (5) years?',
      name: 'BankruptcyInPastFive',
      conditionaler: true // Element below is conditional on this one
    },
    {
      placeholder: 'Please Explain',
      name: 'BankruptcyInPastFiveExplanation',
      label: 'Please Explain',
      conditional: { name: 'BankruptcyInPastFive', value: true },
      maxLength: 500
    },
    {
      Component: Prompt,
      props: {
        options: [
          { name: 'YES', value: true },
          { name: 'NO', value: false }
        ]
      },
      label:
        'Are there currently any open liens levied against your agency and/or partners, officers, or owners?',
      name: 'OpenLiens',
      conditionaler: true // Element below is conditional on this one
    },
    {
      placeholder: 'Please Explain',
      name: 'OpenLiensExplanation',
      label: 'Please Explain',
      conditional: { name: 'OpenLiens', value: true },
      maxLength: 500
    },
    {
      placeholder: 'Enter email',
      name: 'StatementEmail',
      label: 'What email should be used for monthly statements?'
    },
    {
      Component: Select,
      props: {
        options:
          [
            { name: 'AAA/ACG	', value: 'AAA/ACG	' },
            { name: 'ASNOA', value: 'ASNOA' },
            { name: 'Digital Advertisement', value: 'Digital Advertisement' },
            { name: 'Email Campaign', value: 'Email Campaign' },
            { name: 'Facebook', value: 'Facebook' },
            { name: 'First Choice', value: 'First Choice' },
            { name: 'Google Search', value: 'Google Search' },
            { name: 'Horizon Agency Systems', value: 'Horizon Agency Systems' },
            { name: 'Instagram', value: 'Instagram' },
            { name: 'Insurance Journal', value: 'Insurance Journal' },
            { name: 'Iroquois', value: 'Iroquois' },
            { name: 'ISU', value: 'ISU' },
            { name: 'Join the Brokers', value: 'Join the Brokers' },
            { name: 'Kraft Lake', value: 'Kraft Lake' },
            { name: 'LinkedIn', value: 'LinkedIn' },
            { name: 'MyNewMarkets', value: 'MyNewMarkets' },
            { name: 'Pacific Crest', value: 'Pacific Crest' },
            { name: 'Panorama', value: 'Panorama' },
            { name: 'Partner', value: 'Partner' },
            { name: 'Phone Call', value: 'Phone Call' },
            { name: 'PIIB', value: 'PIIB' },
            { name: 'Premier Group', value: 'Premier Group' },
            { name: 'Previously Appointed', value: 'Previously Appointed' },
            { name: 'Referral', value: 'Referral' },
            { name: 'SIAA', value: 'SIAA' },
            { name: 'Smart Choice', value: 'Smart Choice' },
            { name: 'Tik Tok', value: 'Tik Tok' },
            { name: 'Tradeshow', value: 'Tradeshow' },
            { name: 'Twitter', value: 'Twitter' },
            { name: 'United Valley/Renaissance', value: 'United Valley/Renaissance' },
          ],
        placeholder: 'Select One'
      },
      label: 'How did you hear about us?',
      name: 'HowDidYouHearAboutUs',
      keepAsItIs: true,
      conditionaler: "Referral" // Element below is conditional on this one
    },
    {
      label: 'Please specify?',
      placeholder: 'Tell us how you found us!',
      name: 'HowDidYouHearAboutUsOther',
      conditional: { name: 'HowDidYouHearAboutUs', value: "Referral" }
    }
  ];
  return (
    <div className={styles.wrapper}>
      <FormBuilder
        elements={elements}
        schemaShape={schemaShape}
        initialValues={initialValues}
        onNext={props.onNext}
        updateValues={props.updateValues}
        step={props.step}
        setStep={props.setStep}
      />
    </div>
  );
};

export default Step5;
